import { defineStore } from 'pinia'

import {
  ILanguage,
  IMeQuery,
  IMeQueryVariables,
  IRefreshTokenMutation,
  IRole,
  MeDocument,
  RefreshTokenDocument,
} from 'src/graphql'
import { apolloClient } from 'boot/apollo'
import { MessageLanguages } from 'boot/i18n'
import { useConfigStore } from 'stores/config'
import { Page } from 'src/router/routes'
import { jwtDecode } from 'jwt-decode'
import { captureException, endSession, setUser, startSession } from '@sentry/vue'
import { ApolloPersistedStorage } from 'src/utils/apollo/ApolloPersistedStorage'

export interface IAuthState {
  accessToken?: string
  refreshToken?: string
  user?: IMeQuery['me']
  userLoading: boolean
  afterLoginRedirection?: string | null
}

interface ISetAuthArgs {
  accessToken: string
  refreshToken?: string
}

const isTokenExpired = (token: string): boolean => {
  const payload = jwtDecode(token)
  return !payload || (payload.exp ?? 0) * 1000 < Date.now()
}

export const useAuthStore = defineStore('auth', {
  state: (): IAuthState => initAuth(),
  getters: {
    isAuthenticated: ({ accessToken, refreshToken }): boolean =>
      !!accessToken && !!refreshToken && !isTokenExpired(refreshToken),
    me: ({ user }): IMeQuery['me'] | undefined => user,
    meLoading: ({ userLoading }) => userLoading,
    hasRole:
      ({ user }) =>
      (role: IRole) =>
        user && (user.roles.includes(IRole.Admin) || user.roles.includes(role)),
    hasOneOfRoles:
      ({ user }) =>
      (roles: IRole[]) =>
        user &&
        (user.roles.includes(IRole.Admin) || roles.some((role) => user.roles.includes(role))),
    hasActivePass: ({ user }) => !!user?.hasActivePass,
  },
  actions: {
    async authenticate({ accessToken, refreshToken }: ISetAuthArgs) {
      startSession()
      this._setAuth({ accessToken, refreshToken })
      if (this.afterLoginRedirection) {
        const redirect = this.afterLoginRedirection
        this.setAfterSignInRedirection(null)
        await this.router.push(redirect)
      } else {
        await this.router.push({ name: Page.Trips })
      }
    },
    _setAuth({ accessToken, refreshToken }: ISetAuthArgs) {
      localStorage.setItem('accessToken', accessToken)
      this.accessToken = accessToken
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken)
        this.refreshToken = refreshToken
      }
    },
    hasRefreshTokenExpired() {
      if (!this.refreshToken) return false

      return isTokenExpired(this.refreshToken)
    },
    clearAuth() {
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')
      this.accessToken = undefined
      this.refreshToken = undefined
      this.user = undefined
    },
    _setUser(user: IMeQuery['me']) {
      localStorage.setItem('user', JSON.stringify(user))
      this.user = user
      setUser({
        id: user._id,
        email: user.email,
      })

      const languageToLocale: Record<ILanguage, MessageLanguages> = {
        [ILanguage.Cs]: 'cs',
        [ILanguage.En]: 'en',
      }
      const locale = languageToLocale[user.language]
      if (locale) {
        const configStore = useConfigStore()
        if (configStore.language !== locale) {
          configStore.changeLanguage(locale)
        }
      }
    },
    async refetchMe() {
      this.userLoading = true
      try {
        const result = await apolloClient.query<IMeQuery, IMeQueryVariables>({
          query: MeDocument,
          fetchPolicy: 'no-cache',
        })
        if (result?.data?.me) {
          this._setUser(result.data.me)
        }
      } catch (e) {
        console.error('Failed to load current user', e)
      } finally {
        this.userLoading = false
      }
    },
    async refreshAuthToken() {
      try {
        const result = await apolloClient.mutate<IRefreshTokenMutation>({
          mutation: RefreshTokenDocument,
          variables: {
            refreshToken: this.refreshToken,
          },
          fetchPolicy: 'no-cache',
        })
        if (!result.data) {
          return await this.signOut()
        }
        const { accessToken } = result.data.refreshToken
        this._setAuth({ accessToken })
      } catch (e: unknown) {
        captureException(e)
        await this.signOut()
      }
    },
    async signOut({ noRedirect = false } = {}) {
      apolloClient.stop()
      await apolloClient.clearStore()
      this.clearAuth()
      endSession()

      const apolloStorage = new ApolloPersistedStorage()
      await apolloStorage.clear()

      if (noRedirect) return
      await this.router.push({ name: Page.Signpost })
    },
    setAfterSignInRedirection(path: string | null) {
      this.afterLoginRedirection = path
      if (path) {
        localStorage.setItem('afterLoginRedirection', path)
      } else {
        localStorage.removeItem('afterLoginRedirection')
      }
    },
  },
})

function initAuth(): IAuthState {
  const accessToken = localStorage.getItem('accessToken') ?? undefined
  const refreshToken = localStorage.getItem('refreshToken') ?? undefined
  const user = localStorage.getItem('user') ?? undefined
  const afterLoginRedirection = localStorage.getItem('afterLoginRedirection') ?? undefined

  return {
    accessToken,
    refreshToken,
    user: user ? (JSON.parse(user) as IMeQuery['me']) : undefined,
    userLoading: true,
    afterLoginRedirection,
  }
}
